/**
 * Swal title/html gibi innerHTML'e yazılan içerikler için allowlist tabanlı HTML sanitizer.
 *
 * Angular DomSanitizer `style` attribute'unu tamamen sildiği için (uyarı mesajlarında yaygın
 * kullanılan `<span style="color:red;">` gibi biçimlendirmeler kaybolur) ayrı tutulmuştur.
 * İzin verilen etiket/attribute dışındaki her şey; script, event handler (onerror vb.),
 * `javascript:` URL'leri ve `url()`/`expression()` içeren stiller temizlenir.
 */

const ALLOWED_TAGS = new Set([
  'A', 'B', 'BR', 'DIV', 'EM', 'FONT', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HR', 'I', 'LABEL', 'LI',
  'OL', 'P', 'SMALL', 'SPAN', 'STRONG', 'SUB', 'SUP', 'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD',
  'TR', 'U', 'UL'
]);

/** İçeriğiyle birlikte tamamen silinecek etiketler. */
const DROP_WITH_CONTENT_TAGS = new Set([
  'SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'SVG', 'MATH', 'TEXTAREA', 'SELECT'
]);

const ALLOWED_ATTRS = new Set([
  'align', 'class', 'color', 'colspan', 'height', 'href', 'rowspan', 'style', 'target', 'title', 'valign', 'width'
]);

const SAFE_URL = /^(https?:|mailto:|tel:|#|\/(?!\/))/i;
const UNSAFE_STYLE = /url\s*\(|expression\s*\(|javascript:|behavior\s*:|@import|-moz-binding/i;

function cleanNode(node: Node): void {
  Array.from(node.childNodes).forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      node.removeChild(child);
      return;
    }

    const element = child as Element;
    const tag = element.tagName.toUpperCase();

    if (DROP_WITH_CONTENT_TAGS.has(tag)) {
      node.removeChild(element);
      return;
    }

    // İzin verilmeyen etiketin kendisi kaldırılır, metin içeriği korunur.
    if (!ALLOWED_TAGS.has(tag)) {
      cleanNode(element);
      while (element.firstChild) {
        node.insertBefore(element.firstChild, element);
      }
      node.removeChild(element);
      return;
    }

    Array.from(element.attributes).forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim();

      if (!ALLOWED_ATTRS.has(name)
        || (name === 'href' && !SAFE_URL.test(value))
        || (name === 'style' && UNSAFE_STYLE.test(value))) {
        element.removeAttribute(attr.name);
      }
    });

    if (tag === 'A' && element.getAttribute('target') === '_blank') {
      element.setAttribute('rel', 'noopener noreferrer');
    }

    cleanNode(element);
  });
}

export function sanitizeHtml(value: string): string {
  if ((value === null) || (value === undefined) || value === '') {
    return '';
  }

  // Inert doküman: ayrıştırma sırasında script çalışmaz, img/onerror tetiklenmez.
  const doc = document.implementation.createHTMLDocument('');
  const container = doc.createElement('div');
  container.innerHTML = String(value);
  cleanNode(container);
  return container.innerHTML;
}
